import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { IMAGES } from "@/data/products";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us — Shineni India, Denim Manufacturer in Faridabad"
        description="Shineni India is the denim manufacturing brand of Madneel Ventures Pvt. Ltd., producing women's, men's and kids' jeans in Faridabad, Haryana."
      />

      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">About us</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold text-indigo sm:text-5xl">
            A denim brand built on the factory floor
          </h1>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2 lg:items-center">
        <motion.div {...fade}>
          <h2 className="font-display text-2xl font-semibold text-indigo sm:text-3xl">Who we are</h2>
          <p className="mt-5 leading-relaxed text-foreground/75">
            Shineni India is the denim manufacturing and export brand of{" "}
            <strong className="text-indigo">Madneel Ventures Pvt. Ltd.</strong>, based in Faridabad,
            Haryana — one of North India's established garment manufacturing hubs.
          </p>
          <p className="mt-4 leading-relaxed text-foreground/75">
            We manufacture denim jeans for women, men and kids, and run OEM and private-label
            programs for fashion brands. Because buyers work directly with our production team,
            pricing stays factory-direct and communication stays clear — from the first sample
            to the final dispatch.
          </p>
          <p className="mt-4 leading-relaxed text-foreground/75">
            We supply wholesalers and distributors across India and work with international
            importers looking for a dependable Made-in-India denim partner.
          </p>
        </motion.div>
        <motion.div {...fade} className="relative">
          <img src={IMAGES.sewing} alt="Sewing machine needle stitching denim — sample imagery" className="aspect-[4/3] w-full rounded-sm border border-border object-cover" />
          <p className="mt-2 text-xs text-muted-foreground">Representative sample imagery</p>
        </motion.div>
      </section>

      <section className="bg-indigo py-16 text-white sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <motion.h2 {...fade} className="font-display text-2xl font-semibold sm:text-3xl">How we work with buyers</motion.h2>
          <div className="mt-10 grid gap-px overflow-hidden rounded-sm bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "01", t: "Share your requirement", d: "Ranges, quantities, destination and specifications — or a full tech pack." },
              { n: "02", t: "Sampling & approval", d: "We develop samples to your spec and refine until you sign off." },
              { n: "03", t: "Bulk production", d: "Cutting, stitching, washing and finishing under one roof in Faridabad." },
              { n: "04", t: "Quality & dispatch", d: "Checked, packed and dispatched as per the agreed program." },
            ].map((s) => (
              <motion.div key={s.n} {...fade} className="bg-indigo p-8">
                <p className="font-display text-3xl font-semibold text-gold">{s.n}</p>
                <h3 className="mt-4 font-display text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <motion.p {...fade} className="mt-8 max-w-2xl text-sm text-white/60">
            Certifications, compliance documents and program-specific capacity details are shared
            directly with serious buyers on request.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 sm:py-20">
        <motion.h2 {...fade} className="font-display text-2xl font-semibold text-indigo sm:text-3xl">
          Talk to the factory directly
        </motion.h2>
        <motion.div {...fade} className="mt-8">
          <Link to="/request-a-quote" data-testid="about-quote-cta"
            className="rounded-sm bg-gold px-8 py-3.5 text-sm font-semibold text-indigo-deep transition-colors hover:bg-gold-dark">
            Request a Quote
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
